import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import moment from "moment";
import fetchTeamTable from "hooks/getTeamTable";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import teamCodeHashmap from "utils/teamCodeHashmap";
import appConfig from "appConfig";
import "assets/styles.css";
import { isMobile } from "react-device-detect";

const RankPositionChart = ({ teamName, apiDate, gender }) => {
  const [chartData, setChartData] = useState([]);
  const [chartDimensions, setChartDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [error, setError] = useState(null);
  const { instance, accounts } = useMsal(); // MSAL instance for token handling

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setError(null);

        if (!accounts || accounts.length === 0) {
          throw new Error("No account found");
        }

        // Token acquisition request
        const request = {
          scopes: appConfig.authScopes,

          account: accounts[0],
        };

        // Acquire token silently
        const accessTokenResponse = await instance.acquireTokenSilent(request);
        const accessToken = accessTokenResponse.accessToken;

        const teamEntry = teamCodeHashmap[teamName.toLowerCase()];
        const teamCode = teamEntry ? Object.keys(teamEntry)[0] : "";

        if (!teamCode) {
          console.error("Team not found");
          return;
        }

        const data = await fetchTeamTable(
          teamCode,
          apiDate,
          gender,
          -1,
          0,
          accessToken
        );
        const sortedData = data
          .filter((item) => {
            const dateValue = moment(
              item.dateTimeUtc,
              moment.ISO_8601,
              true
            );
            return item.dateTimeUtc && dateValue.isValid();
          })
          .sort(
            (a, b) =>
              moment(a.dateTimeUtc).toDate() -
              moment(b.dateTimeUtc).toDate()
          );

        const formattedData = sortedData.map((item) => ({
          date: moment(item.dateTimeUtc).format("YYYY-MM-DD"),
          value: item.n_WRanking,
        }));

        setChartData(formattedData);
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({
            scopes: appConfig.authScopes,
            account: accounts[0],
          });
        } else {
          setError(error.message);
        }
      }
    };

    fetchChartData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamName, apiDate, gender]);

  useEffect(() => {
    const updateChartDimensions = () => {
      const vw = window.innerWidth;
      const isMobileViewport = window.innerWidth <= 768; 
      setChartDimensions({
        width: (isMobile || isMobileViewport) ? 400 : vw * 0.4, // 40vw
        height: (isMobile || isMobileViewport) ? 200 : vw * 0.23571, // 23.571vw
      });
    };

    updateChartDimensions();
    window.addEventListener("resize", updateChartDimensions);
    return () => window.removeEventListener("resize", updateChartDimensions);
  }, []);

  const maxY = Math.max(...chartData.map((d) => parseFloat(d.value)));
  const minY = Math.min(...chartData.map((d) => parseFloat(d.value)));

  const renderDots = (props) => {
    if (chartData.length <= 20) {
      return (
        <circle
          {...props}
          r={4}
          fill="#FFF"
          stroke="#2732D4"
          strokeWidth={1}
          style={{ flexShrink: 0 }}
        />
      );
    }
    const interval = Math.floor(chartData.length / 20);
    if (props.index % interval === 7) {
      return (
        <circle
          {...props}
          r={4}
          fill="#FFF"
          stroke="#2732D4"
          strokeWidth={1}
          style={{ flexShrink: 0 }}
        />
      );
    }
    return null;
  };

  const scaledStrokeWidth = Math.max(1, chartDimensions.width / 400);

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <LineChart
        width={chartDimensions.width}
        height={chartDimensions.height}
        data={chartData}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="none" />
        <XAxis
          className="RankingPointsChartAxis"
          dataKey="date"
          stroke="#221B70"
          tickFormatter={(date) => moment(date).format("MMM YYYY")}
          interval={Math.floor(chartData.length / 6)}
          tick={{ dy: 10 }}
        />
        <YAxis
          className="RankingPointsChartAxis"
          stroke="#221B70"
          reversed={true}
          domain={[Math.floor(minY / 2) * 2, Math.ceil(maxY / 2) * 2]}
          tickCount={Math.ceil(maxY / 2) - Math.floor(minY / 2) + 1}
          tickFormatter={(tick) => Math.round(tick)}
          padding={{ bottom: 10 }}
        />
        <Tooltip
          contentStyle={{
            fontSize: `${Math.max(10, chartDimensions.width / 40)}px`,
            padding: `${Math.max(1, window.innerWidth * 0.001)}vw`,
          }}
          itemStyle={{
            fontSize: `${Math.max(10, chartDimensions.width / 40)}px`,
          }}
        />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#2732D4"
          name="Rank"
          strokeWidth={scaledStrokeWidth}
          dot={renderDots}
          activeDot={{ r: 4, fill: "#2732D4", stroke: "#2732D4" }} // Active dot settings
        />
      </LineChart>
    </div>
  );
};

RankPositionChart.propTypes = {
  teamName: PropTypes.string.isRequired,
  apiDate: PropTypes.string.isRequired,
  gender: PropTypes.number.isRequired,
};

export default RankPositionChart;
